import { usePracticeDetails } from "@/features/practice";
import { useSSOAccess } from "@/shared/auth/entitlements";
import { useTranslation } from "@/shared/hooks/useTranslation";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/shared/ui/sidebar";
import {
  CalendarIcon,
  UsersIcon,
  ArrowPathIcon,
  UserIcon,
  BeakerIcon,
  ChartPieIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@radix-ui/react-icons";
import { Link, useLocation } from "react-router-dom";

interface NavItem {
  titleKey: string;
  namespace?: string;
  url: string;
  icon: any;
  items?: {
    titleKey: string;
    namespace?: string;
    url: string;
  }[];
}

const navigationItems: NavItem[] = [
  {
    titleKey: "navigation.dashboard",
    namespace: "common",
    url: "/",
    icon: HomeIcon,
  },
  {
    titleKey: "navigation.calendar",
    namespace: "common",
    url: "/calendar",
    icon: CalendarIcon,
  },
  {
    titleKey: "navigation.patients",
    namespace: "common",
    url: "/patients",
    icon: UsersIcon,
  },
  {
    titleKey: "navigation.recalls",
    namespace: "common",
    url: "/recalls",
    icon: ArrowPathIcon,
  },
  {
    titleKey: "navigation.team_hub",
    namespace: "common",
    url: "/team-hub",
    icon: UserIcon,
  },
  {
    titleKey: "navigation.treatments",
    namespace: "common",
    url: "/treatments",
    icon: BeakerIcon,
    items: [
      {
        titleKey: "nav.my_categories",
        namespace: "treatments",
        url: "/treatments/my-categories",
      },
      {
        titleKey: "nav.global_catalog",
        namespace: "treatments",
        url: "/treatments/global-catalog",
      },
      {
        titleKey: "nav.procedures",
        namespace: "treatments",
        url: "/treatments/procedures",
      },
      {
        titleKey: "nav.my_codes",
        namespace: "treatments",
        url: "/treatments/my-codes",
      },
    ],
  },
  {
    titleKey: "navigation.data",
    namespace: "common",
    url: "/data",
    icon: ChartPieIcon,
  },
  {
    titleKey: "navigation.billing_review",
    namespace: "common",
    url: "/billing-review",
    icon: ChartBarIcon,
  },
  {
    titleKey: "navigation.organisation",
    namespace: "common",
    url: "/organisation-settings",
    icon: BuildingOfficeIcon,
    items: [
      {
        titleKey: "navigation.organisations",
        namespace: "common",
        url: "/organisation-settings/organisations",
      },
      {
        titleKey: "navigation.practices",
        namespace: "common",
        url: "/organisation-settings/practices",
      },
      {
        titleKey: "navigation.insurers",
        namespace: "common",
        url: "/insurers",
      },
      {
        titleKey: "navigation.roles",
        namespace: "common",
        url: "/organisation-settings/roles",
      },
      {
        titleKey: "navigation.form_templates",
        namespace: "common",
        url: "/organisation-settings/form-templates",
      },
      {
        titleKey: "navigation.online_booking",
        namespace: "common",
        url: "/organisation-settings/online-booking",
      },
      {
        titleKey: "navigation.communication",
        namespace: "common",
        url: "/organisation-settings/communication",
      },
      {
        titleKey: "navigation.patient_portal",
        namespace: "common",
        url: "/organisation-settings/patient-portal",
      },
      {
        titleKey: "navigation.medidata",
        namespace: "common",
        url: "/organisation-settings/medidata",
      },
    ],
  },
];

export function NavMain() {
  const location = useLocation();
  const { t } = useTranslation(["common", "treatments"]);
  const { data: practiceData } = usePracticeDetails();
  const { allowed: hasSSOAccess } = useSSOAccess();

  // Check if practice is Swiss (for MediData visibility)
  const isSwissPractice = practiceData?.country_code === "CH";

  // Filter navigation items based on practice settings
  const filteredNavigationItems = navigationItems
    .filter((item) => {
      // Hide billing review when billing_mode is "immediate"
      if (item.url === "/billing-review" && practiceData?.billing_mode === "immediate") {
        return false;
      }
      return true;
    })
    .map((item) => {
      // Filter submenu items based on access
      if (item.items) {
        let filteredItems = item.items;

        // Filter out DENTOTAR Catalog when user doesn't have SSO access
        if (!hasSSOAccess) {
          filteredItems = filteredItems.filter(
            (subItem) => subItem.url !== "/treatments/global-catalog"
          );
        }

        // Filter out MediData and Insurers when practice is not Swiss
        if (!isSwissPractice) {
          filteredItems = filteredItems.filter(
            (subItem) =>
              subItem.url !== "/organisation-settings/medidata" &&
              subItem.url !== "/insurers"
          );
        }

        return { ...item, items: filteredItems };
      }
      return item;
    });

  return (
    <SidebarGroup>
      <SidebarMenu>
        {filteredNavigationItems.map((item) => {
          const isActive = location.pathname === item.url;
          const title = t(`${item.namespace}:${item.titleKey}`);

          if (item.items && item.items.length > 0) {
            // Check if any submenu item is active (matches current pathname)
            const isSubmenuActive = item.items.some(
              (subItem) => location.pathname === subItem.url
            );
            const isParentActive = isActive || isSubmenuActive;

            return (
              <Collapsible
                key={item.titleKey}
                asChild
                defaultOpen={isParentActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={title} isActive={isParentActive} asChild>
                      <Link to={item.items[0].url}>
                        {item.icon && <item.icon strokeWidth={1.5} />}
                        <span>{title}</span>
                        <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </Link>
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => {
                        const subTitle = t(`${subItem.namespace}:${subItem.titleKey}`);
                        return (
                          <SidebarMenuSubItem key={subItem.titleKey}>
                            <SidebarMenuSubButton asChild isActive={location.pathname === subItem.url}>
                              <Link to={subItem.url}>
                                <span>{subTitle}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          }

          return (
            <SidebarMenuItem key={item.titleKey}>
              <SidebarMenuButton asChild tooltip={title} isActive={isActive}>
                <Link to={item.url}>
                  {item.icon && <item.icon strokeWidth={1.5} />}
                  <span>{title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
